<script setup lang="ts">
const props = defineProps<{ id: string }>();

defineExpose({
  commands: [],
  properties: {
    id: props.id,
    name: "Report Debugger",
    icon: "lucide:box-select",
  },
});

const report = inject<any>("report", { data: [], loading: false, initialized: false, context: {} });

type DebugPanel = "data" | "context";

const copiedPanel = ref<DebugPanel | null>(null);
let copiedTimer: ReturnType<typeof setTimeout> | undefined;

onBeforeUnmount(() => {
  if (copiedTimer) {
    clearTimeout(copiedTimer);
  }
});

function safeStringify(value: unknown, fallback = "{}") {
  const seen = new WeakSet<object>();

  try {
    const json = JSON.stringify(
      value,
      (_key, currentValue) => {
        if (typeof currentValue === "object" && currentValue !== null) {
          if (seen.has(currentValue)) {
            return "[Circular]";
          }

          seen.add(currentValue);
        }

        return currentValue;
      },
      2,
    );

    return json ?? fallback;
  } catch {
    return fallback;
  }
}

const dataJson = computed(() => safeStringify(report?.data ?? [], "[]"));
const contextJson = computed(() => safeStringify(report?.context ?? {}, "{}"));
const dataCount = computed(() => Array.isArray(report?.data) ? report.data.length : 0);
const contextKeyCount = computed(() => {
  const context = report?.context;

  return context && typeof context === "object" ? Object.keys(context).length : 0;
});
const statusLabel = computed(() => report.loading ? "Loading..." : report.initialized ? "Loaded" : "Not Initialized");
const statusDotClass = computed(() => {
  if (report.loading) {
    return "bg-yellow-500 animate-pulse";
  }

  return report.initialized ? "bg-green-500" : "bg-red-500";
});

async function copyJson(panel: DebugPanel) {
  const text = panel === "data" ? dataJson.value : contextJson.value;

  if (!import.meta.client || !navigator?.clipboard) {
    return;
  }

  await navigator.clipboard.writeText(text);
  copiedPanel.value = panel;

  if (copiedTimer) {
    clearTimeout(copiedTimer);
  }

  copiedTimer = setTimeout(() => {
    copiedPanel.value = null;
  }, 1200);
}
</script>

<template>
  <div class="report-debugger p-4 space-y-4">
    <div class="flex items-center justify-between flex-wrap gap-4">
      <div>
        <h3 class="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-1">
          Report Debug Information
        </h3>
        <div class="flex items-center gap-2">
          <div
            :class="[
              'w-3 h-3 rounded-full',
              statusDotClass,
            ]"
          ></div>
          <span class="text-sm text-gray-600 dark:text-gray-400">
            {{ statusLabel }}
          </span>
        </div>
      </div>

      <div class="grid gap-2 text-sm text-gray-600 dark:text-gray-400 sm:grid-cols-2 lg:grid-cols-4">
        <div class="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 dark:border-gray-700 dark:bg-gray-800">
          <div class="text-[10px] font-semibold uppercase tracking-wide text-gray-400">
            Kind
          </div>
          <div class="font-medium text-gray-800 dark:text-gray-200">
            {{ report.kind || 'unknown' }}
          </div>
        </div>
        <div class="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 dark:border-gray-700 dark:bg-gray-800">
          <div class="text-[10px] font-semibold uppercase tracking-wide text-gray-400">
            File
          </div>
          <div class="max-w-52 truncate font-medium text-gray-800 dark:text-gray-200" :title="report.fileName || ''">
            {{ report.fileName || '—' }}
          </div>
        </div>
        <div class="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 dark:border-gray-700 dark:bg-gray-800">
          <div class="text-[10px] font-semibold uppercase tracking-wide text-gray-400">
            Data rows
          </div>
          <div class="font-medium text-gray-800 dark:text-gray-200">
            {{ dataCount }}
          </div>
        </div>
        <div class="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 dark:border-gray-700 dark:bg-gray-800">
          <div class="text-[10px] font-semibold uppercase tracking-wide text-gray-400">
            Context keys
          </div>
          <div class="font-medium text-gray-800 dark:text-gray-200">
            {{ contextKeyCount }}
          </div>
        </div>
      </div>
    </div>

    <Tabs default-value="data" class="w-full">
      <TabsList>
        <TabsTrigger value="data">
          Data
        </TabsTrigger>
        <TabsTrigger value="context">
          Context
        </TabsTrigger>
      </TabsList>

      <TabsContent value="data" class="mt-4">
        <Card class="overflow-hidden">
          <div class="flex items-center justify-between gap-3 border-b border-gray-200 bg-gray-100 px-4 py-2 dark:border-gray-700 dark:bg-gray-800">
            <div>
              <div class="text-sm font-mono text-gray-700 dark:text-gray-300">
                report.data JSON
              </div>
              <div class="text-xs text-gray-500 dark:text-gray-400">
                Mapped output consumed by child report components.
              </div>
            </div>
            <Button size="sm" variant="outline" @click="copyJson('data')">
              <icon mode="svg" size="12" :name="copiedPanel === 'data' ? 'lucide:check' : 'lucide:copy'" class="mr-1" />
              {{ copiedPanel === 'data' ? 'Copied' : 'Copy' }}
            </Button>
          </div>
          <pre class="max-h-[32rem] overflow-auto whitespace-pre-wrap break-words bg-white p-4 font-mono text-xs leading-relaxed text-gray-800 dark:bg-gray-950 dark:text-gray-200">{{ dataJson }}</pre>
        </Card>
      </TabsContent>

      <TabsContent value="context" class="mt-4">
        <Card class="overflow-hidden">
          <div class="flex items-center justify-between gap-3 border-b border-gray-200 bg-gray-100 px-4 py-2 dark:border-gray-700 dark:bg-gray-800">
            <div>
              <div class="text-sm font-mono text-gray-700 dark:text-gray-300">
                report.context JSON
              </div>
              <div class="text-xs text-gray-500 dark:text-gray-400">
                Raw inputs available to templating and mapper functions.
              </div>
            </div>
            <Button size="sm" variant="outline" @click="copyJson('context')">
              <icon mode="svg" size="12" :name="copiedPanel === 'context' ? 'lucide:check' : 'lucide:copy'" class="mr-1" />
              {{ copiedPanel === 'context' ? 'Copied' : 'Copy' }}
            </Button>
          </div>
          <pre class="max-h-[32rem] overflow-auto whitespace-pre-wrap break-words bg-white p-4 font-mono text-xs leading-relaxed text-gray-800 dark:bg-gray-950 dark:text-gray-200">{{ contextJson }}</pre>
        </Card>
      </TabsContent>
    </Tabs>
  </div>
</template>

<script lang="ts">
import { ComponentDefinitionIds } from "#bb/lib/componentDefinitionIds";
export default {
  componentId: ComponentDefinitionIds.REPORT_PROVIDER_DEBUGGER,
  id: "report-provider-debugger",
  icon: "lucide:box-select",
  type: "Report Debugger",
  description:
    "Report Debugger component for debugging report data providers.",
  default: { },
  categories: ["Reports"],
  order: 12004,
};
</script>
